/**
 * Service Worker Registration
 * Handles registration, updates, and lifecycle of the service worker
 */

interface ServiceWorkerConfig {
  onSuccess?: (registration: globalThis.ServiceWorkerRegistration) => void;
  onUpdate?: (registration: globalThis.ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

/**
 * Register the service worker
 */
export async function register(config?: ServiceWorkerConfig): Promise<void> {
  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service workers are not supported');
    return;
  }

  // Only register in production or if explicitly enabled
  const isDev = import.meta.env.DEV;
  const forceEnable = import.meta.env.VITE_ENABLE_SW === 'true';
  
  if (isDev && !forceEnable) {
    console.log('[SW] Service worker disabled in development');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('[SW] Service worker registered successfully:', registration);

    // Check for updates periodically
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000); // Check every hour

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      if (!newWorker) {
        return;
      }

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker available
          console.log('[SW] New service worker available');
          
          if (config?.onUpdate) {
            config.onUpdate(registration);
          } else {
            // Default behavior: reload the page
            if (confirm('New version available! Reload to update?')) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
              window.location.reload();
            }
          }
        }
      });
    });

    // Service worker is controlling the page
    if (navigator.serviceWorker.controller) {
      console.log('[SW] Service worker is controlling the page');
      config?.onSuccess?.(registration);
    }

    // Listen for controller change (new service worker activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[SW] Controller changed, reloading page');
      window.location.reload();
    });

  } catch (error) {
    console.error('[SW] Service worker registration failed:', error);
    config?.onError?.(error as Error);
  }
}

/**
 * Unregister the service worker
 */
export async function unregister(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.unregister();
    console.log('[SW] Service worker unregistered');
  } catch (error) {
    console.error('[SW] Error unregistering service worker:', error);
  }
}

/**
 * Check if the service worker is supported
 */
export function isSupported(): boolean {
  return 'serviceWorker' in navigator;
}

/**
 * Get the current service worker registration
 */
export async function getRegistration(): Promise<globalThis.ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    return await navigator.serviceWorker.ready;
  } catch (error) {
    console.error('[SW] Error getting service worker registration:', error);
    return null;
  }
}
